import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Progress } from "@/components/ui/progress";
import { TrendingUp, ClipboardCheck } from "lucide-react";

export default function Editors() {
  const [editors, setEditors] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/editors");
      setEditors(data);
    })();
  }, []);

  const maxLoad = Math.max(1, ...editors.map((e) => e.active_tasks_count + e.completed_tasks_count));

  return (
    <div className="p-8 lg:p-12">
      <PageHeader
        eyebrow="07 / Bandwidth"
        title="Editors"
        subtitle="Efficiency snapshots. Active load, deadlines met, output volume — spot burnout before it hits."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {editors.length === 0 && (
          <div className="col-span-3 text-sm text-zinc-500 rounded-xl border border-white/10 bg-zinc-900 p-10 text-center">
            No editors yet — seed demo or invite editors by having them sign in with Google.
          </div>
        )}
        {editors.map((e) => {
          const total = e.active_tasks_count + e.completed_tasks_count;
          const load = Math.min(100, Math.round((e.active_tasks_count / Math.max(3, maxLoad)) * 100));
          return (
            <div
              key={e.user_id}
              data-testid={`editor-card-${e.user_id}`}
              className="rounded-xl border border-white/10 bg-zinc-900 p-6"
            >
              <div className="flex items-center gap-4">
                {e.picture ? (
                  <img src={e.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-zinc-800" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-lg leading-tight">{e.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5 truncate">
                    {e.email}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="rounded-lg border border-white/5 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                    <ClipboardCheck className="h-3 w-3" /> Active
                  </div>
                  <div className="mt-1 font-display text-2xl font-bold">{e.active_tasks_count}</div>
                </div>
                <div className="rounded-lg border border-white/5 p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Delivered
                  </div>
                  <div className="mt-1 font-display text-2xl font-bold">{e.completed_tasks_count}</div>
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    Bandwidth
                  </span>
                  <span className="text-xs text-zinc-400">
                    {e.active_tasks_count > 4 ? "Heavy" : e.active_tasks_count > 2 ? "Steady" : "Light"}
                  </span>
                </div>
                <Progress value={load} className="h-1.5 bg-white/5" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
